import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Guion del negocio (Mensajería Etapa 2, 2026-08-20) ───────────────────────
// El guion aprobado es la única fuente con la que Evo Brain responde mensajes.
// Hasta que se aprueba, lo generado no se usa para nada.

export type ContextStatus = 'draft' | 'approved' | 'superseded';
export type FactOrigin = 'generated' | 'edited' | 'manual';

export interface ContextFact {
  id: number;
  contextId: number;
  topic: string;
  statement: string;
  /** null cuando origin es 'manual' — lo escribió una persona, no el sitio. */
  sourceUrl: string | null;
  sourceQuote: string | null;
  origin: FactOrigin;
  sortOrder: number;
}

export interface BusinessContext {
  id: number;
  status: ContextStatus;
  sourceRootUrl: string | null;
  generatedAt: string | null;
  approvedAt: string | null;
  approvedBy: number | null;
  facts: ContextFact[];
}

export interface BusinessContextState {
  websiteUrl: string | null;
  approved: BusinessContext | null;
  draft: BusinessContext | null;
}

export interface GenerateResult {
  context: BusinessContext;
  scrape: { read: string[]; failed: Array<{ url: string; reason: string }> };
  proposed: number;
  rejected: number;
}

const KEY = ['business-context'];

export function useBusinessContext() {
  return useQuery<BusinessContextState>({
    queryKey: KEY,
    queryFn: () => api.get('/api/business-context'),
    staleTime: 60_000,
  });
}

export function useSaveWebsiteUrl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (websiteUrl: string) =>
      api.put<{ websiteUrl: string | null }>('/api/business-context/website-url', { websiteUrl }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

/** Tarda: baja hasta 6 páginas con pausa entre cada una y después llama a
 *  Claude. Quien la use tiene que mostrar que está trabajando. */
export function useGenerateContext() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (websiteUrl?: string) =>
      api.post<GenerateResult>('/api/business-context/generate', websiteUrl ? { websiteUrl } : {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useApproveContext() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<BusinessContext>(`/api/business-context/${id}/approve`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useAddFact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contextId, ...body }: { contextId: number; topic?: string; statement: string }) =>
      api.post<ContextFact>(`/api/business-context/${contextId}/facts`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useUpdateFact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ factId, statement }: { factId: number; statement: string }) =>
      api.patch<ContextFact>(`/api/business-context/facts/${factId}`, { statement }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useDeleteFact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (factId: number) => api.delete<{ ok: boolean }>(`/api/business-context/facts/${factId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}
